import React, { useEffect, useState, useCallback, useRef } from 'react';
import { gallery } from '../../data/gallery';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Maximize2, 
  Image as ImageIcon,
  Grid3X3,
  Grid2X2,
  Sparkles,
  Building2,
  HeartHandshake,
  Users,
  Stethoscope,
  ArrowRight,
  ZoomIn,
  LayoutGrid,
  Square
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import PageHero from '../PageHero';

const Gallery = () => {
  const { t, language } = useLanguage();
  const [filter, setFilter] = useState('All');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [gridCols, setGridCols] = useState('3'); // '2', '3', or '4' columns (desktop)
  const [mobileView, setMobileView] = useState('grid'); // 'grid' (2-col) or 'feed' (1-col) for mobile only

  // Touch swipe tracking for mobile lightbox
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const thumbnailContainerRef = useRef(null);

  const rawCategories = ['All', 'Hospital & Clinics', 'Camps & Outreach', 'Our Team & Doctors', 'Patient Care & OPD'];

  // Category Highlights for Mobile Story Bar
  const categoryHighlights = [
    {
      id: 'All',
      label: language === 'mr' ? 'सर्व फोटो' : (language === 'kn' ? 'ಎಲ್ಲಾ' : 'All'),
      img: gallery[0]?.url,
      count: gallery.length
    },
    {
      id: 'Hospital & Clinics',
      label: language === 'mr' ? 'रुग्णालय' : (language === 'kn' ? 'ಆಸ್ಪತ್ರೆ' : 'Hospital'),
      img: gallery.find(g => g.category === 'Hospital & Clinics')?.url || gallery[0]?.url,
      count: gallery.filter(g => g.category === 'Hospital & Clinics').length
    },
    {
      id: 'Camps & Outreach',
      label: language === 'mr' ? 'शिबिरे' : (language === 'kn' ? 'ಶಿಬಿರಗಳು' : 'Camps'),
      img: gallery.find(g => g.category === 'Camps & Outreach')?.url || gallery[1]?.url,
      count: gallery.filter(g => g.category === 'Camps & Outreach').length
    },
    {
      id: 'Our Team & Doctors',
      label: language === 'mr' ? 'डॉक्टर्स' : (language === 'kn' ? 'ವೈದ್ಯರು' : 'Doctors'),
      img: gallery.find(g => g.category === 'Our Team & Doctors')?.url || gallery[2]?.url,
      count: gallery.filter(g => g.category === 'Our Team & Doctors').length
    },
    {
      id: 'Patient Care & OPD',
      label: language === 'mr' ? 'ओपीडी' : (language === 'kn' ? 'ಆರೈಕೆ' : 'OPD'),
      img: gallery.find(g => g.category === 'Patient Care & OPD')?.url || gallery[3]?.url,
      count: gallery.filter(g => g.category === 'Patient Care & OPD').length
    }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredGallery = filter === 'All' 
    ? gallery 
    : gallery.filter(item => item.category === filter);

  const handlePrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : filteredGallery.length - 1));
  }, [filteredGallery.length]);

  const handleNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setLightboxIndex((prev) => (prev < filteredGallery.length - 1 ? prev + 1 : 0));
  }, [filteredGallery.length]);

  const handleClose = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, handleClose, handlePrev, handleNext]);

  // Lock body scroll when lightbox is active
  useEffect(() => {
    if (lightboxIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [lightboxIndex]);

  // Scroll active thumbnail into view inside lightbox
  useEffect(() => {
    if (lightboxIndex !== null && thumbnailContainerRef.current) {
      const activeThumb = thumbnailContainerRef.current.children[lightboxIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [lightboxIndex]);

  // Touch Swipe handlers for mobile lightbox
  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 45; // pixels

    if (distance > minSwipeDistance) {
      handleNext();
    } else if (distance < -minSwipeDistance) {
      handlePrev();
    }
  };

  const changeFilter = (category) => {
    setFilter(category);
    setLightboxIndex(null);
  };

  const getCategoryCount = (cat) => {
    if (cat === 'All') return gallery.length;
    return gallery.filter(item => item.category === cat).length;
  };

  // Localized icon for categories
  const getCategoryIcon = (cat) => {
    switch (cat) {
      case 'Hospital & Clinics':
        return <Building2 size={15} />;
      case 'Camps & Outreach':
        return <HeartHandshake size={15} />;
      case 'Our Team & Doctors':
        return <Users size={15} />;
      case 'Patient Care & OPD':
        return <Stethoscope size={15} />;
      default:
        return <Sparkles size={15} />;
    }
  };

  // Localized label for categories
  const getCategoryLabel = (cat) => {
    if (cat === 'All') return t('gallerySection.all', 'All');
    
    if (language === 'mr') {
      if (cat === 'Hospital & Clinics') return 'रुग्णालय व शाखा';
      if (cat === 'Camps & Outreach') return 'आरोग्य शिबिरे';
      if (cat === 'Our Team & Doctors') return 'डॉक्टर्स व टीम';
      if (cat === 'Patient Care & OPD') return 'रुग्णसेवा व ओपीडी';
    } else if (language === 'kn') {
      if (cat === 'Hospital & Clinics') return 'ಆಸ್ಪತ್ರೆ ಮತ್ತು ಕ್ಲಿನಿಕ್';
      if (cat === 'Camps & Outreach') return 'ಆರೋಗ್ಯ ಶಿಬಿರಗಳು';
      if (cat === 'Our Team & Doctors') return 'ವೈದ್ಯರು ಮತ್ತು ತಂಡ';
      if (cat === 'Patient Care & OPD') return 'ರೋಗಿಗಳ ಆರೈಕೆ';
    }
    return cat;
  };

  const currentImage = lightboxIndex !== null ? filteredGallery[lightboxIndex] : null;

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      {/* 1. Attractive Hero Banner matching Branches.js */}
      <PageHero
        badge={t('gallerySection.badge')}
        title={t('gallerySection.title')}
        subtitle={t('gallerySection.pageSubtitle')}
      />

      {/* 2. Main Container Overlapping Hero */}
      <div className="container mx-auto px-4 lg:px-8 -mt-16 relative z-20 pb-24">

        {/* Mobile View: Quick Categories Card in place of pill chips */}
        <div className="block md:hidden mb-5">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl p-3.5 border border-slate-200/90 shadow-sm">
            {/* Header: Title, Live Photo Count & Layout Switcher */}
            <div className="flex items-center justify-between mb-3 pb-2.5 border-b border-gray-100">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-teal animate-pulse"></span>
                <span className="text-xs font-extrabold uppercase tracking-wider text-teal">
                  {language === 'mr' ? 'कॅटेगरी निवडा' : (language === 'kn' ? 'ವರ್ಗಗಳು' : 'Quick Categories')}
                </span>
                <span className="text-[11px] font-bold text-gray-400 font-mono ml-1">
                  ({filteredGallery.length})
                </span>
              </div>

              {/* Mobile Layout Switcher: Grid vs Feed */}
              <div className="flex items-center bg-gray-100 p-0.5 rounded-xl gap-1">
                <button
                  onClick={() => setMobileView('grid')}
                  className={`px-2 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer ${
                    mobileView === 'grid' ? 'bg-white text-navy shadow-xs' : 'text-gray-500 hover:text-navy'
                  }`}
                  title="2 Columns Grid"
                >
                  <LayoutGrid size={13} />
                  <span className="text-[10px]">Grid</span>
                </button>
                <button
                  onClick={() => setMobileView('feed')}
                  className={`px-2 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer ${
                    mobileView === 'feed' ? 'bg-white text-navy shadow-xs' : 'text-gray-500 hover:text-navy'
                  }`}
                  title="1 Column Feed"
                >
                  <Square size={13} />
                  <span className="text-[10px]">Feed</span>
                </button>
              </div>
            </div>

            {/* Quick Categories Story Reel */}
            <div className="flex items-center gap-3 overflow-x-auto pb-1 pt-0.5 px-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {categoryHighlights.map((cat) => {
                const isActive = filter === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => changeFilter(cat.id)}
                    className="flex flex-col items-center shrink-0 w-[64px] group focus:outline-none cursor-pointer active:scale-95 transition-transform"
                  >
                    <div className={`relative p-0.5 rounded-full transition-all duration-300 ${
                      isActive 
                        ? 'bg-gradient-to-tr from-teal via-clinical to-navy shadow-md shadow-teal/25 scale-105' 
                        : 'bg-transparent border-2 border-slate-200 hover:border-teal/50'
                    }`}>
                      <div className="p-0.5 bg-white rounded-full">
                        <img
                          src={cat.img}
                          alt={cat.label}
                          className="w-12 h-12 rounded-full object-cover shrink-0"
                        />
                      </div>
                      <span className={`absolute -bottom-0.5 -right-0.5 px-1.5 py-0.2 rounded-full text-[9px] font-black border border-white leading-none ${
                        isActive ? 'bg-clinical text-white' : 'bg-slate-700 text-white'
                      }`}>
                        {cat.count}
                      </span>
                    </div>
                    <span className={`text-[11px] font-bold mt-1.5 w-full truncate text-center ${
                      isActive ? 'text-navy font-extrabold' : 'text-gray-500'
                    }`}>
                      {cat.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Desktop & Laptop View: Original Classic Pill Filter Bar */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="hidden md:block bg-white rounded-3xl p-3 sm:p-4 shadow-sm border border-gray-200 mb-8 sm:mb-12"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            
            {/* Scrollable Category Filter Chips */}
            <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {rawCategories.map((category) => {
                const count = getCategoryCount(category);
                const isActive = filter === category;
                return (
                  <button
                    key={category}
                    onClick={() => changeFilter(category)}
                    className={`relative inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap transition-colors duration-200 shrink-0 cursor-pointer ${
                      isActive ? 'text-white' : 'text-gray-600 hover:text-navy hover:bg-gray-100'
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeGalleryFilterPill"
                        className="absolute inset-0 bg-navy rounded-full shadow-md shadow-navy/20"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10 flex items-center gap-1.5">
                      <span className={isActive ? 'text-teal' : 'text-gray-400'}>
                        {getCategoryIcon(category)}
                      </span>
                      <span>{getCategoryLabel(category)}</span>
                    </span>
                    <span
                      className={`relative z-10 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-bold ${
                        isActive ? 'bg-clinical text-white' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Desktop View Switcher (3 vs 4 Col) & Counter */}
            <div className="flex items-center justify-between w-full md:w-auto gap-4 pt-2 md:pt-0 border-t md:border-t-0 border-gray-100">
              <span className="text-xs font-semibold text-gray-500">
                {language === 'mr' ? `${filteredGallery.length} फोटो दाखवले आहेत` : `Showing ${filteredGallery.length} photos`}
              </span>

              <div className="flex items-center bg-gray-100 p-1 rounded-xl gap-1">
                <button
                  onClick={() => setGridCols('3')}
                  className={`p-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 cursor-pointer ${
                    gridCols === '3' ? 'bg-white text-navy shadow-xs' : 'text-gray-500 hover:text-navy'
                  }`}
                  title="3 Columns Grid"
                >
                  <Grid3X3 size={15} />
                  <span className="text-[11px]">3 Col</span>
                </button>
                <button
                  onClick={() => setGridCols('4')}
                  className={`p-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 cursor-pointer ${
                    gridCols === '4' ? 'bg-white text-navy shadow-xs' : 'text-gray-500 hover:text-navy'
                  }`}
                  title="4 Columns Grid"
                >
                  <Grid2X2 size={15} />
                  <span className="text-[11px]">4 Col</span>
                </button>
              </div>
            </div>

          </div>
        </motion.div>

        {/* 3. Creative Animated Gallery Grid (2-col or 1-col on Mobile, 3 or 4-col on Desktop) */}
        <motion.div 
          layout
          className={`grid ${
            mobileView === 'grid' ? 'grid-cols-2 gap-2.5 sm:gap-6' : 'grid-cols-1 gap-3.5 sm:gap-6'
          } ${
            gridCols === '4'
              ? 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
              : 'sm:grid-cols-2 lg:grid-cols-3'
          }`}
        >
          <AnimatePresence>
            {filteredGallery.map((item, index) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.92, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 15 }}
                transition={{ duration: 0.35, delay: Math.min(index * 0.03, 0.3) }}
                key={item.id}
                onClick={() => setLightboxIndex(index)}
                className={`group relative ${
                  mobileView === 'feed' ? 'aspect-[16/10] sm:aspect-[4/3]' : 'aspect-[4/3]'
                } rounded-xl sm:rounded-2xl overflow-hidden bg-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 cursor-pointer border border-gray-200/80 active:scale-[0.97]`}
              >
                {/* Image */}
                <img 
                  src={item.url} 
                  alt={item.title} 
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700 ease-out" 
                />

                {/* Subtle Gradient Overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/35 to-transparent opacity-85 sm:opacity-60 group-hover:opacity-95 transition-opacity duration-300"></div>

                {/* Content Container */}
                <div className="absolute inset-0 p-2.5 sm:p-5 flex flex-col justify-between z-10">
                  
                  {/* Top Bar: Category Pill & Zoom Icon */}
                  <div className="flex items-center justify-between gap-1">
                    <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 py-0.5 sm:px-3 sm:py-1 rounded-full bg-navy/85 group-hover:bg-clinical text-white text-[9px] sm:text-xs font-bold uppercase tracking-wider backdrop-blur-md shadow-xs transition-colors truncate max-w-[80%] sm:max-w-none">
                      {getCategoryIcon(item.category)}
                      <span className="truncate">{getCategoryLabel(item.category)}</span>
                    </span>

                    {/* Visible on Mobile, Hover on Desktop */}
                    <span className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/25 sm:bg-white/20 group-hover:bg-white text-white group-hover:text-navy backdrop-blur-md flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300 shadow-xs sm:shadow-md shrink-0">
                      <ZoomIn className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </span>
                  </div>

                  {/* Center Floating Icon Prompt (Desktop only) */}
                  <div className="hidden sm:block self-center opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-75 transition-all duration-300 pointer-events-none">
                    <span className="px-3.5 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-white text-xs font-semibold flex items-center gap-1.5 border border-white/30 shadow-lg">
                      <Maximize2 size={13} />
                      <span>{language === 'mr' ? 'मोठे पहा' : 'View Full'}</span>
                    </span>
                  </div>

                  {/* Bottom Bar: Title */}
                  <div>
                    <h3 className="text-white font-bold text-xs sm:text-base leading-tight sm:leading-snug line-clamp-2 drop-shadow-md group-hover:text-teal transition-colors">
                      {item.title}
                    </h3>
                  </div>

                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

      </div>

      {/* 5. Full-Screen Cinematic Lightbox Modal */}
      <AnimatePresence>
        {currentImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={handleClose}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col justify-between p-3 sm:p-6 select-none"
          >
            {/* Top Bar */}
            <div 
              className="flex items-center justify-between w-full max-w-6xl mx-auto z-10 pt-2 pb-3 border-b border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-clinical text-white text-[11px] sm:text-xs font-bold uppercase tracking-wider">
                  {getCategoryIcon(currentImage.category)}
                  <span>{getCategoryLabel(currentImage.category)}</span>
                </span>
                <span className="text-gray-300 text-xs sm:text-sm font-semibold">
                  {lightboxIndex + 1} / {filteredGallery.length}
                </span>
              </div>

              {/* Touch-friendly close button */}
              <button
                onClick={handleClose}
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/15 text-white/90 hover:text-white hover:bg-white/25 flex items-center justify-center transition-all focus:outline-none active:scale-95 cursor-pointer shadow-md"
                aria-label="Close modal"
              >
                <X size={22} />
              </button>
            </div>

            {/* Main Image Area with Touch Swipe Support */}
            <div 
              className="relative flex-1 flex items-center justify-center max-w-6xl w-full mx-auto my-2 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Prev Button */}
              <button
                onClick={handlePrev}
                className="absolute left-2 sm:left-4 z-20 w-11 h-11 sm:w-13 sm:h-13 rounded-full bg-black/60 sm:bg-white/10 hover:bg-white/25 text-white backdrop-blur-md flex items-center justify-center transition-all duration-200 focus:outline-none hover:scale-105 active:scale-95 shadow-xl border border-white/15 cursor-pointer"
                aria-label="Previous photo"
              >
                <ChevronLeft size={28} />
              </button>

              {/* Active Image with Animated Entrance */}
              <motion.div
                key={currentImage.id}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.25 }}
                className="max-h-[58vh] sm:max-h-[66vh] flex items-center justify-center p-2"
              >
                <img
                  src={currentImage.url}
                  alt={currentImage.title}
                  className="max-h-[58vh] sm:max-h-[66vh] max-w-[92vw] sm:max-w-[82vw] object-contain rounded-2xl shadow-2xl border border-white/10"
                />
              </motion.div>

              {/* Next Button */}
              <button
                onClick={handleNext}
                className="absolute right-2 sm:right-4 z-20 w-11 h-11 sm:w-13 sm:h-13 rounded-full bg-black/60 sm:bg-white/10 hover:bg-white/25 text-white backdrop-blur-md flex items-center justify-center transition-all duration-200 focus:outline-none hover:scale-105 active:scale-95 shadow-xl border border-white/15 cursor-pointer"
                aria-label="Next photo"
              >
                <ChevronRight size={28} />
              </button>
            </div>

            {/* Bottom Section: Caption & Interactive Filmstrip Thumbnails */}
            <div 
              className="w-full max-w-6xl mx-auto text-center pt-2 pb-2 z-10 space-y-3"
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <h2 className="text-white text-sm sm:text-base md:text-lg font-bold tracking-wide px-4 line-clamp-1 drop-shadow-sm">
                  {currentImage.title}
                </h2>
                <p className="text-gray-400 text-[11px] sm:text-xs mt-0.5">
                  <span className="sm:hidden">👈 Swipe left or right to browse</span>
                  <span className="hidden sm:inline">Use &larr; and &rarr; arrow keys to navigate &bull; Esc to close</span>
                </p>
              </div>

              {/* Filmstrip Thumbnail Row */}
              <div 
                ref={thumbnailContainerRef}
                className="flex items-center justify-center gap-2 overflow-x-auto py-1 px-4 max-w-2xl mx-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {filteredGallery.map((thumb, idx) => (
                  <button
                    key={thumb.id}
                    onClick={() => setLightboxIndex(idx)}
                    className={`w-11 h-11 sm:w-13 sm:h-13 rounded-lg overflow-hidden shrink-0 transition-all duration-200 cursor-pointer border-2 ${
                      lightboxIndex === idx
                        ? 'border-teal scale-110 shadow-lg shadow-teal/30'
                        : 'border-white/20 opacity-40 hover:opacity-100 hover:border-white/60'
                    }`}
                  >
                    <img 
                      src={thumb.url} 
                      alt="" 
                      className="w-full h-full object-cover" 
                    />
                  </button>
                ))}
              </div>
            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
